import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import { deleteRequest, getRequest, patchRequest, postRequest } from 'api/apiClient';
import { OpButton } from 'components/customAntd/DLS/OpButton/OpButton';
import { OpModal } from 'components/customAntd/DLS/OpModal/OpModal';
import { IOnSubmitArgs, OpForm } from 'components/customAntd/DLS/OpForm/OpForm';
import { DATE_TIME_AM_PM_FORMAT, DATE_TIME_FORMAT } from 'constants/dates';
import { useConfirmModal } from 'utils/customHooks/useConfirmModal';
import dayjs from 'dayjs';
import { Empty, List, message, notification, Spin } from 'antd';
import { OpRow } from 'components/customAntd/DLS/OpRow/OpRow';
import { OpCol } from 'components/customAntd/DLS/OpCol/OpCol';
import { LoadingOutlined } from '@ant-design/icons';
import { CopyOutlined } from '@ant-design/icons';
import STATUS from 'constants/status';
import { hasPermission } from 'utils/utils';
import { triggerGuestPassUpdate } from 'store/slices/guestPassSlice';
import { useTranslation } from 'react-i18next';

const GuestPassTab: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();

    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const globalUserId = useSelector((state: RootState) => state.users.globalUser?.id);

    const { selectedVisit } = useSelector((state: RootState) => state.visits);

    const [guestPassData, setGuestPassData] = useState<any>();
    const [guestPassPINDefault, setGuestPassPINDefault] = useState<boolean>(false);
    const [defaultEntryIds, setDefaultEntryIds] = useState<number[]>([]);
    const [entryIdOptions, setEntryIdOptions] = useState<{ value: number; label: string }[]>([]);
    const [isGenerateGuestPassModalOpen, setIsGenerateGuestPassModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const [generateguestPassForm] = OpForm.useForm();
    const [guestPassForm] = OpForm.useForm();

    const confirmModal = useConfirmModal();
    const { t } = useTranslation();

    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
    const hasGuestpassWrite = hasPermission(tokenScopeList, orgId, 'o', 'guestpass:w');

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const integrationData = await getRequest(`/orgs/${orgId}/integrationMsiAlta`);
                const initialData = integrationData.data[0];
                setGuestPassPINDefault(initialData.guestPassPINDefault === 1);
                if (initialData.entryIds) {
                    setDefaultEntryIds(initialData.entryIds.map((id: string) => Number(id)));
                }

                const guestPass = await getRequest(`/orgs/${orgId}/visitor/${selectedVisit?.id}/msiAltaGuestPass`);
                if (Array.isArray(guestPass.data) && guestPass.data.length !== 0) {
                    const modifiedGuestPass = {
                        ...guestPass.data[0],
                        startUtc: guestPass.data[0].startUtc ? dayjs(guestPass.data[0].startUtc).utc(true).local() : undefined,
                        endUtc: guestPass.data[0].startUtc ? dayjs(guestPass.data[0].endUtc).utc(true).local() : undefined
                    }
                    setGuestPassData(modifiedGuestPass);
                }

                const listAllEntriesResponse = await getRequest(`/orgs/${orgId}/integrationMsiAltaEntry`);
                const defaultEntryList = listAllEntriesResponse.data
                    .map((defaultEntry: any) => ({
                        value: defaultEntry.id,
                        label: defaultEntry.name,
                    }));
                setEntryIdOptions(defaultEntryList);
            } catch (error) {
                console.log(error);
                console.error("Failed to fetch data.");
            } finally {
                setLoading(false);
            }
        };

        if (orgId && selectedVisit?.id) {
            fetchData();
        }
    }, [orgId, selectedVisit?.id]);

    const handleSubmit = (async ({ values }: IOnSubmitArgs) => {
        setIsSubmitting(true);
        try {
            // update
            if (guestPassData !== undefined) {
                const submittingValues = {
                    startUtc: dayjs(values.startUtc).utc().format(DATE_TIME_FORMAT),
                    endUtc: dayjs(values.endUtc).utc().format(DATE_TIME_FORMAT),
                    entryId: values.entryId,
                    PIN: (values.guestPassPINDefault === true)
                        ? (guestPassData.PIN === null || guestPassData.PIN === "")
                            ? Math.floor(1000 + Math.random() * 9000).toString()
                            : guestPassData.PIN
                        : '',
                    autoSignIn: values.autoSignIn ? 1 : 0
                };
                if (submittingValues.endUtc && submittingValues.startUtc && dayjs(submittingValues.endUtc).isBefore(dayjs(submittingValues.startUtc))) {
                    notification.error({
                        message: t('notification.error'),
                        description: t('visitors.guestPass.endBeforeStartError'),
                        placement: 'bottomRight',
                    });
                    return;
                }

                // Edit Guest Pass
                await patchRequest(`/orgs/${orgId}/visitor/${selectedVisit?.id}/msiAltaGuestPass`, submittingValues);

            } else { // create
                const submittingValues = {
                    startUtc: dayjs(values.startUtc).local().utc().format(DATE_TIME_FORMAT),
                    endUtc: dayjs(values.endUtc).local().utc().format(DATE_TIME_FORMAT),
                    entryId: values.entryId,
                    userId: globalUserId,
                    PIN: values.guestPassPINDefault === true ? Math.floor(1000 + Math.random() * 9000).toString() : undefined,
                    autoSignIn: values.autoSignIn ? 1 : 0
                };
                if (submittingValues.endUtc && submittingValues.startUtc && dayjs(submittingValues.endUtc).isBefore(dayjs(submittingValues.startUtc))) {
                    notification.error({
                        message: t('notification.error'),
                        description: t('visitors.guestPass.endBeforeStartError'),
                        placement: 'bottomRight',
                    });
                    return;
                }

                // Generate Guest Pass
                await postRequest(`/orgs/${orgId}/visitor/${selectedVisit?.id}/msiAltaGuestPass`, submittingValues);
            }
            const guestPass = await getRequest(`/orgs/${orgId}/visitor/${selectedVisit?.id}/msiAltaGuestPass`);

            if (!guestPass || !Array.isArray(guestPass.data) || guestPass.data.length === 0) {
                setGuestPassData(undefined);
                notification.error({
                    message: t('notification.error'),
                    description: t('visitors.guestPass.submissionError'),
                    placement: 'bottomRight',
                });
                return;
            }

            const modifiedGuestPass = {
                ...guestPass.data[0],
                startUtc: guestPass.data[0].startUtc ? dayjs(guestPass.data[0].startUtc).utc(true).local() : undefined,
                endUtc: guestPass.data[0].startUtc ? dayjs(guestPass.data[0].endUtc).utc(true).local() : undefined
            }
            setGuestPassData(modifiedGuestPass);
            notification.success({
                message: t('notification.success'),
                description: t('visitors.guestPass.updateSuccess'),
                placement: 'bottomRight',
            });

            dispatch(triggerGuestPassUpdate());// inform guest pass is updated for guest pass report page

        } catch (error) {
            notification.error({
                message: t('notification.error'),
                description: t('visitors.guestPass.submissionError'),
                placement: 'bottomRight',
            });
            console.error("Form submission failed:", error);
        } finally {
            setIsSubmitting(false);
        }
    });

    const finalOnCancel = () => {
        if (generateguestPassForm.isFieldsTouched()) {
            confirmModal({
                title: t('confirmDiscard.title'),
                content: t('confirmDiscard.message'),
                okText: t('confirmDiscard.ok'),
                cancelText: t('confirmDiscard.cancel'),
                onOk: () => {
                    generateguestPassForm.resetFields();
                    setIsGenerateGuestPassModalOpen(false);
                },
            });
        } else {
            setIsGenerateGuestPassModalOpen(false);
        }
    };

    const handleRevoke = () => {
        try {
            confirmModal({
                title: t('visitors.guestPass.confirmRevoke.title'),
                content: t('visitors.guestPass.confirmRevoke.message'),
                okText: t('visitors.guestPass.confirmRevoke.ok'),
                cancelText: t('visitors.guestPass.confirmRevoke.cancel'),
                onOk: async () => {
                    await deleteRequest(`/orgs/${orgId}/visitor/${selectedVisit?.id}/msiAltaGuestPass`);
                    notification.success({
                        message: t('notification.success'),
                        description: t('visitors.guestPass.revokeSuccess'),
                        placement: 'bottomRight',
                    });
                    setGuestPassData(undefined); // Reset the guest pass data
                    dispatch(triggerGuestPassUpdate()); // inform guest pass is updated for guest pass report page
                },
            });
        } catch (error) {
            console.log(`Failed to revoke guest pass.`, error);
        }
    }

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            message.success(t('visitors.guestPass.copySuccess'));
        }).catch((error) => {
            message.error(t('visitors.guestPass.copyFail'));
            console.error('Copy failed', error);
        });
    };

    const isGuestPassActive = useCallback(() => {
        if (guestPassData?.startUtc && guestPassData?.endUtc) {
            const now = dayjs();
            return now.isAfter(guestPassData.startUtc) && now.isBefore(guestPassData.endUtc);
        }
        return false;
    }, [guestPassData]);

    const hasValidVisitor = selectedVisit?.visitors?.some(
        (visitor) => visitor.status !== STATUS.DENIED_ENTRY.id && visitor.status !== STATUS.SIGNED_OUT.id
    );

    const getDefaultGuestPassTime = () => {
        const now = dayjs();
        const scheduleStart = selectedVisit?.scheduleStart ? dayjs(selectedVisit.scheduleStart) : null;
        const scheduleEnd = selectedVisit?.scheduleEnd ? dayjs(selectedVisit.scheduleEnd) : null;
        const statusId = selectedVisit?.visitStatus?.id;

        if (statusId === STATUS.PENDING.id) {
            if (scheduleStart && now.isBefore(scheduleStart)) {
                return { startUtc: scheduleStart, endUtc: scheduleEnd || scheduleStart.add(1, 'hour') };
            } else if (scheduleStart && scheduleEnd && now.isAfter(scheduleStart) && now.isBefore(scheduleEnd)) {
                return { startUtc: now, endUtc: scheduleEnd };
            } else if (scheduleEnd && now.isAfter(scheduleEnd)) {
                return { startUtc: now, endUtc: now.add(1, 'hour') };
            }
        } else if (statusId === STATUS.SIGNED_IN.id) {
            if (scheduleStart && scheduleEnd && now.isBefore(scheduleEnd)) {
                return { startUtc: now, endUtc: scheduleEnd };
            }
            return { startUtc: now, endUtc: now.add(1, 'hour') };
        } else if (statusId === STATUS.SIGNED_OUT.id) {
            return { startUtc: now, endUtc: now.add(1, 'hour') };
        }
        return { startUtc: now, endUtc: now.add(1, 'hour') }; // Default case
    };

    const defaultGuestPassTime = getDefaultGuestPassTime();
    return (
        <>
            {(loading || isSubmitting) ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Spin indicator={<LoadingOutlined spin />} />
                </div>
            ) : (
                <>
                    {guestPassData === undefined ? (
                        <>
                            {(hasGuestpassWrite && hasValidVisitor) ? (
                                <OpButton
                                    type="primary" ghost
                                    style={{ width: "100%" }}
                                    onClick={() => {
                                        setIsGenerateGuestPassModalOpen(true);
                                    }}
                                >
                                    {t('visitors.guestPass.generate')}
                                </OpButton>
                            ) : (
                                <Empty description={t('visitors.guestPass.empty')} />
                            )}
                        </>
                    ) : (
                        <>
                            <OpForm
                                form={guestPassForm}
                                onSubmit={() => { }}
                                initialValues={guestPassData}
                                hasError={false}
                                defaultButtons={false}
                                isReadOnly={true}
                            >
                                {!isGuestPassActive() && (
                                    <div style={{
                                        backgroundColor: 'grey',
                                        color: 'white',
                                        padding: '10px 0',
                                        textAlign: 'center',
                                        borderRadius: '16px',
                                        fontWeight: 'bold',
                                        marginBottom: '16px'
                                    }}>
                                        {t('visitors.guestPass.status.invalid')}
                                    </div>
                                )}

                                {isGuestPassActive() && (
                                    <div style={{
                                        backgroundColor: '#709A7A',
                                        color: 'white',
                                        padding: '10px 0',
                                        textAlign: 'center',
                                        borderRadius: '16px',
                                        fontWeight: 'bold',
                                        marginBottom: '16px'
                                    }}>
                                        {t('visitors.guestPass.status.valid')}
                                    </div>
                                )}

                                <OpRow gutter={24}>
                                    <OpCol span={12}>
                                        <OpForm.DatePicker
                                            format={DATE_TIME_AM_PM_FORMAT}
                                            showTime
                                            minuteStep={5}
                                            label={t('visitors.guestPass.validFrom')}
                                            name="startUtc"
                                            rules={[{ required: true, message: t('visitors.guestPass.validation.startDateRequired') }]}
                                        />
                                    </OpCol>
                                    <OpCol span={12}>
                                        <OpForm.DatePicker
                                            format={DATE_TIME_AM_PM_FORMAT}
                                            showTime
                                            minuteStep={5}
                                            label={t('visitors.guestPass.validTo')}
                                            name="endUtc"
                                            rules={[{ required: true, message: t('visitors.guestPass.validation.endDateRequired') }]}
                                        />
                                    </OpCol>
                                </OpRow>
                                <List
                                    header={t('visitors.guestPass.url')}
                                    dataSource={[{ label: t('visitors.guestPass.url') }]}
                                    bordered
                                    renderItem={item => (
                                        <List.Item>
                                            <div style={{ width: '100%' }}>
                                                {/* Display Guest Pass URL */}
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => e.preventDefault()}
                                                        style={{ color: '#1890ff', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline' }}
                                                    >
                                                        {`https://control.invisit.us/guestpass/${guestPassData.urlToken}`}
                                                    </button>
                                                    <div
                                                        onClick={() => copyToClipboard(
                                                            guestPassData.PIN
                                                                ? `https://control.invisit.us/guestpass/${guestPassData.urlToken}\nPIN: ${guestPassData.PIN}`
                                                                : `https://control.invisit.us/guestpass/${guestPassData.urlToken}`
                                                        )}
                                                        style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                                                    >
                                                        <CopyOutlined style={{ marginRight: '4px' }} />
                                                        {t('visitors.guestPass.copy')}
                                                    </div>
                                                </div>

                                                {guestPassData.PIN && (
                                                    <div style={{ marginTop: '8px' }}>
                                                        {t('visitors.guestPass.pin')}: {guestPassData.PIN}
                                                    </div>
                                                )}
                                            </div>
                                        </List.Item>
                                    )}
                                />
                                {(hasGuestpassWrite) && (
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                                        <OpButton
                                            type="primary"
                                            onClick={() => {
                                                setIsGenerateGuestPassModalOpen(true);
                                            }}
                                            style={{
                                                minWidth: '160px',
                                                width: 'auto',
                                                marginRight: '16px',
                                            }}
                                        >
                                            {t('visitors.guestPass.edit')}
                                        </OpButton>
                                        <OpButton
                                            type="primary" danger
                                            onClick={handleRevoke}
                                            style={{
                                                minWidth: '160px',
                                                width: 'auto',
                                            }}
                                        >
                                            {t('visitors.guestPass.revoke')}
                                        </OpButton>
                                    </div>
                                )}
                            </OpForm>
                        </>
                    )}

                    {isGenerateGuestPassModalOpen && (
                        <OpModal
                            title={guestPassData ? t('visitors.guestPass.edit') : t('visitors.guestPass.generate')}
                            open={isGenerateGuestPassModalOpen}
                            onClose={finalOnCancel}
                            onOk={() => {
                                generateguestPassForm
                                    .validateFields()
                                    .then(() => {
                                        generateguestPassForm.submit();
                                        setIsGenerateGuestPassModalOpen(false);
                                    })
                                    .catch((errorInfo) => {
                                        notification.error({
                                            message: t('notification.error'),
                                            description: t('visitors.guestPass.validationError'),
                                            placement: 'bottomRight',
                                        });
                                    });
                            }}
                            onCancel={finalOnCancel}
                            okText={t('visitors.guestPass.ok')}
                            cancelText={t('visitors.guestPass.cancel')}
                            centered
                        >
                            <OpForm
                                form={generateguestPassForm}
                                onSubmit={handleSubmit}
                                hasError={false}
                                defaultButtons={false}
                                initialValues={guestPassData ? {
                                    startUtc: guestPassData.startUtc,
                                    endUtc: guestPassData.endUtc,
                                    entryId: guestPassData.msiAltaEntryId?.map((entry: any) => entry.id) || [],
                                    guestPassPINDefault: (guestPassData.PIN === null || guestPassData.PIN === "") ? false : true,
                                    autoSignIn: guestPassData.autoSignIn
                                } : {
                                    startUtc: defaultGuestPassTime.startUtc,
                                    endUtc: defaultGuestPassTime.endUtc,
                                    entryId: defaultEntryIds,
                                    guestPassPINDefault: guestPassPINDefault
                                }}
                            >
                                <OpForm.DatePicker
                                    format={DATE_TIME_AM_PM_FORMAT}
                                    showTime
                                    minuteStep={5}
                                    label={t('visitors.guestPass.validFrom')}
                                    name="startUtc"
                                    rules={[{ required: true, message: t('visitors.guestPass.startDateRequired') }]}
                                    onChange={!guestPassData ? (value) => {
                                        if (value) {
                                            const endUtcValue = dayjs(value).add(1, 'hour');
                                            generateguestPassForm.setFieldsValue({
                                                endUtc: endUtcValue,
                                            });
                                        }
                                    } : undefined}
                                />
                                <OpForm.DatePicker
                                    format={DATE_TIME_AM_PM_FORMAT}
                                    showTime
                                    minuteStep={5}
                                    label={t('visitors.guestPass.validTo')}
                                    name="endUtc"
                                    rules={[{ required: true, message: t('visitors.guestPass.endDateRequired') }]}
                                />
                                <OpForm.Select
                                    name={"entryId"}
                                    label={t('visitors.guestPass.entries')}
                                    placeholder={t('visitors.guestPass.entriesPlaceholder')}
                                    mode="multiple"
                                    options={entryIdOptions}
                                    rules={[{ required: true, message: t('visitors.guestPass.entriesRequired') }]}
                                />
                                <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                    <OpForm.Switch
                                        name={"guestPassPINDefault"}
                                    />
                                    <span style={{ marginLeft: 8 }}>{t('visitors.guestPass.pinToggle')}</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                    <OpForm.Switch
                                        name={"autoSignIn"}
                                        onChange={(checked: boolean) => {
                                            if (checked) {
                                                confirmModal({
                                                    title: t('visitors.guestPass.confirmAutoSignIn.title'),
                                                    content: t('visitors.guestPass.confirmAutoSignIn.message'),
                                                    okText: t('visitors.guestPass.confirmAutoSignIn.ok'),
                                                    cancelText: t('visitors.guestPass.confirmAutoSignIn.cancel'),
                                                    onOk: () => {
                                                        generateguestPassForm.setFieldsValue({ autoSignIn: true });
                                                    },
                                                    onCancel: () => {
                                                        generateguestPassForm.setFieldsValue({ autoSignIn: false });
                                                    },
                                                });
                                            } else {
                                                generateguestPassForm.setFieldsValue({ autoSignIn: false });
                                            }
                                        }}
                                    />
                                    <span style={{ marginLeft: 8 }}>{t('visitors.guestPass.autoSignInToggle')}</span>
                                </div>
                            </OpForm>
                        </OpModal>
                    )}
                </>
            )}
        </>
    )
};

export default GuestPassTab;
